import React, {type ReactNode, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {CacheProvider} from '@emotion/react'
import {CookieBanner} from 'components/CookieBanner'
import ErrorOverlayMonitor from 'components/errorMonitors/ErrorOverlayMonitor'
import NetworkMonitor from 'components/errorMonitors/NetworkMonitor'
import InitializeABTesting from 'components/InitializeABTesting'
import {PasswordlessLoginWrapper} from 'components/PasswordlessLoginWrapper'
import {SyncRuntimeToStore} from 'components/SyncRuntimeToStore'
import UnknownErrorBoundary from 'components/UnknownErrorBoundary'
import {useInstallFullStory} from 'hooks/useInstallFullStory'
import {
  getAnonymousId,
  getDeviceType,
  getIsRtlLanguage,
  getIsSyntheticTest,
  getLocale,
  getUserCountryWithFallback
} from 'modules/meta/selectors'
// eslint-disable-next-line no-restricted-imports
import * as Opticks from 'opticks'

import {
  DeviceLayoutProvider,
  deviceTypeToBreakpoint
} from '@vio/atlas/context/deviceLayout'
import GridGlobalStyles from '@vio/atlas/foundation/GridGlobalStyles'
import {CssBaseline} from '@vio/atlas/helpers/CssBaseline'
import FontsLoaderNext from '@vio/atlas/src/components/designSystem/helpers/FontsLoaderNext/FontsLoaderNext'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {RouterProvider} from '@vio/core/src/_web/router/context/RouterProviderNext'
import {evaluateInRuntime} from '@vio/core/src/_web/utils/css/emotion-rtl'
import {TeamContextProvider} from '@vio/core/src/analytics/components/TeamContext'
import {Team} from '@vio/core/src/analytics/types/Events'
import DummyOpticksProvider from '@vio/core/src/experiments/components/DummyOpticksProvider'
import OpticksProvider, {
  OpticksContext
} from '@vio/core/src/experiments/components/OpticksProvider'
import ConfigureI18n, {
  type ConfigureI18nProps
} from '@vio/core/src/localization/components/ConfigureI18n'
import {getIsoptimizelyDisabled} from '@vio/debug-panel'
import {EmailSignUpProvider} from '@vio/shared/src/authentication/Email/EmailSignUpProvider'
import {EmailFlow} from '@vio/shared/src/authentication/Email/index'
import {SusiProvider} from '@vio/shared/src/authentication/SUSI/bottomSheet/SusiBottomSheet/SusiProvider'
import {CapabilityRuntimeProvider} from '@vio/shared/src/whiteLabel/CapabilityRuntimeProvider'

interface Props
  extends Pick<
    ConfigureI18nProps,
    'preloadedMessages' | 'preloadedCalendarStrings'
  > {
  children: ReactNode
}

/**
 * `useToggle` and the store's `toggle` use the server toggle during server
 * render. The dummy provider only covers code that calls the Opticks singleton
 * directly, which gets the A side.
 */
const UniversalOpticksProvider = ({children}: {children: JSX.Element}) => {
  const anonymousId = useSelector(getAnonymousId)
  const isSyntheticTest = useSelector(getIsSyntheticTest)
  if (
    typeof window === 'undefined' ||
    isSyntheticTest // Disable opticks for website synthetics
  ) {
    return (
      <DummyOpticksProvider userId={anonymousId ?? 'checkout'}>
        <OpticksContext.Provider value={Opticks}>
          {children}
        </OpticksContext.Provider>
      </DummyOpticksProvider>
    )
  }

  const forceDisableOptimizelyLog = getIsoptimizelyDisabled()

  return (
    <OpticksProvider
      // The datafile is preloaded by getServerSideProps
      dataFileUrl={undefined}
      disableOptimizelyLogs={forceDisableOptimizelyLog}
    >
      {children}
    </OpticksProvider>
  )
}

const AppProviders = ({
  children,
  preloadedMessages,
  preloadedCalendarStrings
}: Props) => {
  const {brand} = useBrand()
  const userCountry = useSelector(getUserCountryWithFallback)
  // Seeded on the server with the same URL, cookie, default order the client uses
  const locale = useSelector(getLocale)
  const isRtl = useSelector(getIsRtlLanguage)
  const emotionCache = useMemo(() => evaluateInRuntime(isRtl), [isRtl])
  useInstallFullStory()

  const anonymousId = useSelector(getAnonymousId)
  const deviceType = useSelector(getDeviceType)

  return (
    <>
      <UniversalOpticksProvider>
        <InitializeABTesting>
          <ConfigureI18n
            languageCode={locale}
            countryCode={userCountry}
            preloadedMessages={preloadedMessages}
            preloadedCalendarStrings={preloadedCalendarStrings}
          >
            <CacheProvider value={emotionCache}>
              <DeviceLayoutProvider
                updateOnMount
                value={deviceTypeToBreakpoint(deviceType)}
              >
                <CapabilityRuntimeProvider>
                  <SyncRuntimeToStore />
                  <CssBaseline />
                  <GridGlobalStyles />
                  <NetworkMonitor />
                  <FontsLoaderNext languageCode={locale} theme={brand.theme} />
                  <UnknownErrorBoundary>
                    <RouterProvider>
                      <SusiProvider
                        brand={brand}
                        userCountryCode={userCountry}
                        anonymousId={anonymousId}
                      >
                        {children}
                      </SusiProvider>
                      <ErrorOverlayMonitor />
                      <PasswordlessLoginWrapper
                        brand={brand}
                        languageCode={locale}
                      />
                      <TeamContextProvider team={Team.Select}>
                        <CookieBanner />
                      </TeamContextProvider>

                      <EmailSignUpProvider
                        languageCode={locale}
                        apiUrl={
                          process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT!
                        }
                      >
                        <EmailFlow />
                      </EmailSignUpProvider>
                    </RouterProvider>
                  </UnknownErrorBoundary>
                </CapabilityRuntimeProvider>
              </DeviceLayoutProvider>
            </CacheProvider>
          </ConfigureI18n>
        </InitializeABTesting>
      </UniversalOpticksProvider>
    </>
  )
}

export default AppProviders
